import {
  type EmailDisplayRegion,
  foldQuotedHistory,
  type QuoteFold,
  type QuoteMessage,
} from "./quoted-history.ts";

/*
 * What the reading frame shows of one email, prepared once per thread revision: its quoted history
 * folded (`quoted-history.ts`) and its paragraph spacing made the author's own.
 *
 * Classic Outlook writes a blank line as an empty `<p class="MsoNormal">&nbsp;</p>` and zeroes every
 * paragraph's margin in its own stylesheet, which the sanitizer rightly removes. Left with a
 * browser's default margins, each blank line became about 50 px, and a signature table built from
 * them opened gaps of several hundred pixels. Mail that spaces itself with blank paragraphs gets
 * its paragraph margins removed (`data-lcr-tight`); other mail keeps the default.
 */

export type EmailDisplay = {
  html: string;
  /** Folded nodes are omitted before images acquire sources; CSS hiding alone still fetches. */
  visibleHtml: string;
  fold: QuoteFold;
  regions: readonly EmailDisplayRegion[];
};

function blank(paragraph: Element): boolean {
  return (
    (paragraph.textContent ?? "").replaceAll(" ", " ").trim() === "" &&
    paragraph.querySelector("img, table, hr") === null
  );
}

export function prepareEmailDisplay(
  sanitizedHtml: string,
  earlier: readonly QuoteMessage[],
  parse: (html: string) => Document,
): EmailDisplay {
  const fold = foldQuotedHistory(sanitizedHtml, earlier, parse);
  const regions: readonly EmailDisplayRegion[] =
    fold.kind === "folded" ? fold.regions : [{ kind: "visible", html: sanitizedHtml }];
  const visibleHtml =
    fold.kind === "folded"
      ? regions
          .filter((region) => region.kind === "visible")
          .map((region) => region.html)
          .join("")
      : fold.html;
  return {
    html: withSpacing(fold.html, parse),
    visibleHtml: withSpacing(visibleHtml, parse),
    fold,
    regions,
  };
}

function withSpacing(html: string, parse: (html: string) => Document): string {
  try {
    const document = parse(html);
    const paragraphs = [...document.body.querySelectorAll("p")];
    if (!paragraphs.some(blank)) return html;
    for (const paragraph of paragraphs) paragraph.setAttribute("data-lcr-tight", "");
    return document.body.innerHTML;
  } catch {
    return html;
  }
}
